import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { useHistory } from 'react-router';
import LoadingScreen from '../../components/muiLegacy/LoadingScreen/LoadingScreen';
import VoxelAlert from '../../components/voxel/Alert/VoxelAlert';
import VoxelButton from '../../components/voxel/Buttons/VoxelButton';
import VoxelPageContainer from '../../components/voxel/Containers/VoxelPageContainer';
import VoxelSelect from '../../components/voxel/Forms/VoxelSelect';
import VoxelLink from '../../components/voxel/Link/VoxelLink';
import VoxelPanel from '../../components/voxel/Surfaces/VoxelPanel';
import VoxelText from '../../components/voxel/Text/VoxelText';
import { usersApiService } from '../../graphql/users/usersApiService';
import { useActiveTabContext } from '../../logic/activeTabContext/useActiveTabContext';
import LicenseIncreaseWarningDialog from '../TeamPage/SubscriptionPage/LicenseIncreaseWarningDialog';
import { useLicenseIncreaseWarning } from '../TeamPage/SubscriptionPage/useLicenseIncreaseWarning';
import { TeamMenuChoices } from '../TeamPage/TeamPage';

export interface ConnectionInvitationPageProps {
  token: string;
}

export const ConnectionInvitationPage: React.FC<ConnectionInvitationPageProps> = ({ token }) => {
  // Get data
  const cleanedToken = token?.replaceAll('_', '+').replaceAll('-', '/');
  const { status, teamConnection, alternativeUserIds, query } =
    usersApiService.queries.useGetConnectionInvitation({
      invitationToken: cleanedToken,
    });

  const { teamConnections } = usersApiService.queries.useGetTeamConnections({});
  const { user, availableUsers, team } = usersApiService.queries.useGetCurrentUser();
  const relevantUsers = availableUsers?.filter((au) => alternativeUserIds?.includes(au.id));

  // Change team logic
  const activeTabContext = useActiveTabContext();

  // Accept invitation
  const [mutating, setMutating] = useState(false);
  const acceptMutation = usersApiService.mutations.useAcceptConnectionInvitation();
  const history = useHistory();

  const invitationType = !!teamConnection?.sharingTeam ? 'receive' : 'share';
  const licenseIncreaseWarningBag = useLicenseIncreaseWarning(team);
  const handleAccept = () => {
    const submitFn = () => {
      setMutating(true);
      return acceptMutation({ invitationToken: cleanedToken })
        .then(() => history.push('/team/' + TeamMenuChoices.Connections))
        .catch(() => setMutating(false));
    };
    if (invitationType === 'receive') {
      licenseIncreaseWarningBag.handleAttemptToAddChildTeams(submitFn);
    } else {
      submitFn();
    }
  };

  if (!status || !relevantUsers) {
    return <LoadingScreen />;
  }

  // Alternative status screens
  const externalTeam = teamConnection?.receivingTeam ?? teamConnection?.sharingTeam;

  let statusTitle = '';
  let statusText = '';
  let showTeamSelect = false;

  if (status === 'not found') {
    // Case : not found
    statusTitle = 'Not found';
    statusText = 'We could not find the invite';
  } else if (user.team.voxelTeam) {
    // Case : Voxel team
    statusTitle = 'Not enabled for voxel team';
    statusText = 'Voxel team cannot use invites';
    showTeamSelect = true;
  } else if (status === 'invalid') {
    // Case : invite invalid
    statusTitle = 'Invite invalid';
    statusText = 'This invite is not valid';
  } else if (status === 'consumed') {
    // Case : invite consumed
    statusTitle = 'Invite used';
    statusText = 'This invite has already been used';
  } else if (status === 'expired') {
    // Case : invite expired
    statusTitle = 'Invite used';
    statusText = 'This invite has already been used';
  } else if (externalTeam?.id === team?.id) {
    // Case : Same team
    showTeamSelect = true;
    statusTitle = 'Choose another team';
    statusText = 'This invitation was made by your current team';
  } else if (!user.team.accessUserCanEditTeamConnections) {
    // Case : Insufficient permissions
    showTeamSelect = true;
    statusTitle = 'Insufficient permissions';
    statusText =
      "You do not have the permission to accept the invitation. Share the link with your team's admins.";
  } else if (
    invitationType === 'receive' &&
    teamConnections?.some((ct) => ct.sharingTeam?.id === externalTeam?.id)
  ) {
    // Case : Connection exists (as receiver)
    showTeamSelect = true;
    statusTitle = 'Choose another team';
    statusText = "Your team already has access to this team's project data";
  } else if (
    invitationType === 'share' &&
    teamConnections?.some((ct) => ct.receivingTeam?.id === externalTeam?.id)
  ) {
    // Case : No paid subscription
    showTeamSelect = true;
    statusTitle = 'Choose another team';
    statusText = 'Your team is already sharing its project data with this team';
  } else if (invitationType === 'receive' && !user.team.canAccessChildTeams) {
    showTeamSelect = true;
    statusTitle = 'Receiving connections not available';
    statusText = "Only paid subscription plans can receive other team's data";
  }

  if (!!statusText?.length) {
    return (
      <VoxelPageContainer>
        <div className="flex flex-row items-center justify-center">
          <div className="w-[640px] flex flex-col gap-4">
            <VoxelAlert>{statusText}</VoxelAlert>
            {!!relevantUsers?.length && showTeamSelect && (
              <div className="w-40">
                <VoxelSelect
                  label="Change team"
                  labelPosition="top"
                  options={relevantUsers.map((u) => ({ title: u.team.name, value: u.team.id }))}
                  selectedOptionValue={team.id}
                  setSelectedOption={(opt) => {
                    activeTabContext.setCurrentTeamId(opt.value);
                  }}
                />
              </div>
            )}
            {showTeamSelect && !!teamConnection?.receivingTeam && !teamConnection.sharingTeam && (
              <VoxelLink href={`/register/${token}`}>... or sign up with a new team</VoxelLink>
            )}
          </div>
        </div>
      </VoxelPageContainer>
    );
  }

  if (!externalTeam) {
    return <LoadingScreen />;
  }

  return (
    <>
      <LicenseIncreaseWarningDialog licenseWarningBag={licenseIncreaseWarningBag} />
      <VoxelPageContainer>
        <div className="flex flex-col w-full items-center justify-center gap-12">
          <VoxelPanel className="w-[500px]" shadowClass="shadow-md">
            <div className="flex flex-col gap-8 p-8 items-center">
              <div>
                <VoxelText variant="page subtitle" align="center">
                  {invitationType === 'share' && "Share your team's project data"}
                  {invitationType === 'receive' && `Access ${externalTeam.name}'s project data`}
                </VoxelText>
                <VoxelText disabled div align="center" className="pt-3">
                  {invitationType === 'share' && (
                    <>
                      {teamConnection.createdByUser?.email} of team
                      <span className="px-1.5 py-0.5 rounded-lg bg-voxel-blue-50 mx-1 text-voxel-blue-600 text-xs">
                        {externalTeam.name}
                      </span>{' '}
                      wants to read all of
                      <span className="px-1.5 py-0.5 rounded-lg bg-voxel-blue-50 mx-1 text-voxel-blue-600 text-xs">
                        {team.name}'s
                      </span>{' '}
                      project data.
                    </>
                  )}

                  {invitationType === 'receive' && (
                    <>
                      {teamConnection.createdByUser?.email} of team
                      <span className="px-1.5 py-0.5 rounded-lg bg-voxel-blue-50 mx-1 text-voxel-blue-600 text-xs">
                        {externalTeam.name}
                      </span>{' '}
                      offers to share all of their project data with your team{' '}
                      <span className="px-1.5 py-0.5 rounded-lg bg-voxel-blue-50 mx-1 text-voxel-blue-600 text-xs">
                        {team.name}
                      </span>
                    </>
                  )}
                </VoxelText>
              </div>
              <div>
                <VoxelButton
                  title={invitationType === 'share' ? 'Share our data' : 'Accept'}
                  color="primary"
                  className="w-64"
                  size="xl"
                  startIcon={<div />}
                  disabled={mutating}
                  endIcon={<FontAwesomeIcon icon={faCheck} />}
                  onClick={handleAccept}
                />
              </div>
            </div>
          </VoxelPanel>
          <div className="flex flex-row items-end flex-nowrap gap-4">
            {!!relevantUsers?.length && (
              <VoxelSelect
                label="... choose another team for connection"
                labelPosition="top"
                inputWidthClass="w-64"
                options={relevantUsers.map((u) => ({ title: u.team.name, value: u.team.id }))}
                selectedOptionValue={team.id}
                size="sm"
                disabled={mutating}
                setSelectedOption={(opt) => {
                  activeTabContext.setCurrentTeamId(opt.value);
                }}
              />
            )}
            {!!teamConnection?.receivingTeam && !teamConnection.sharingTeam && (
              <div className="py-1 text-voxel-gray-700 text-xs">
                <VoxelLink href={`/register/${token}`}>
                  ... or create a new team for connection
                </VoxelLink>
              </div>
            )}
          </div>
        </div>
      </VoxelPageContainer>
    </>
  );
};

export default ConnectionInvitationPage;
